import { lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router';
import { CatalogueTable } from './catalogue-table';
import { RecommendationOverview } from './recommendation-overview';
import { Button } from '@/components/ui/button';
import { useRecommendationSettings } from '@/hooks/use-product-recommendations';
import type { RecommendationClient, SelectionKind } from '@/types';
const Products = lazy(() => import('@/routes/training/products'));

export function RecommendationSettings({ client, scope, canEdit = true }: { client: RecommendationClient; scope: string; canEdit?: boolean }) {
  const { config, error, saving, message, save, retry } = useRecommendationSettings(client, scope);
  const [params, setParams] = useSearchParams();
  const requestedKind = params.get('catalogue');
  const kind: SelectionKind | null = requestedKind === 'productIds' || requestedKind === 'tags' || requestedKind === 'collectionIds' ? requestedKind : null;
  const setKind = (value: SelectionKind | null, mode?: 'promoted' | 'excluded'): void => setParams((current) => {
    if (value) current.set('catalogue', value);
    else current.delete('catalogue');
    if (mode) current.set('recommendationMode', mode);
    else if (!value) current.delete('recommendationMode');
    return current;
  });
  if (error) return <div className='space-y-3 p-5'><p role='alert'>{error}</p><Button onClick={retry}>Retry</Button></div>;
  if (!config) return <p role='status'>Loading recommendation settings…</p>;
  const productId = params.get('productId');
  if (productId && kind) return <Suspense fallback={<p role='status'>Loading product…</p>}><Products key={`${scope}:${productId}`} initialProductId={productId} onBack={() => setParams((current) => { current.delete('productId'); return current; })} /></Suspense>;
  return <div className='flex min-h-0 flex-1 flex-col bg-white'>
    {kind === null ? <RecommendationOverview config={config} onOpen={setKind} /> : <>
      <CatalogueTable key={kind} initialKind={kind} onBack={() => setKind(null)} onKindChange={(value) => setKind(value)} initialMode={params.get('recommendationMode') === 'excluded' ? 'excluded' : 'promoted'} config={config} onCommit={save} client={client} scope={scope} canEdit={canEdit && !saving} onProductSelect={(id) => setParams((current) => { current.set('productId', id); return current; })} />
    </>}
    {message && <div className='px-5 py-3'>
      <p role='status' className='text-xs text-gray-800'>{message}</p>
    </div>}
  </div>;
}
